import { enrichedColumnSchema, } from '../interfaces/enrichedModuleSchema.js';
import { snakeCaseToLabel } from '../utils/strings.js';
export class EnrichedColumn {
    parentModel;
    type;
    typeConstraints;
    guarded;
    hidden;
    cast;
    searchable;
    displayName;
    displayNamePlural;
    order;
    name;
    forRelation;
    fakerMethod;
    /**
     * Constructs an EnrichedColumn instance from the provided parameters.
     *
     * This constructor initializes all column properties including type information,
     * display formatting, behavioral flags, and enriched metadata. The enriched column
     * extends the base column functionality with additional properties for relations
     * and faker methods used in data generation.
     *
     * @param params - The constructor parameters containing all column configuration.
     */
    constructor(params, parentModel) {
        this.parentModel = parentModel;
        this.type = params.type;
        this.displayName = params.displayName;
        this.displayNamePlural = params.displayNamePlural;
        this.order = params.order;
        this.typeConstraints = params.typeConstraints ?? [];
        this.guarded = params.guarded;
        this.hidden = params.hidden;
        this.cast = params.cast;
        this.searchable = params.searchable;
        this.name = params.name;
        this.forRelation = params.forRelation;
        this.fakerMethod = params.fakerMethod;
    }
    /**
     * Instantiates an EnrichedColumn from a JSON string.
     *
     * This method parses the provided JSON string, which should represent an enriched column object,
     * and constructs a new EnrichedColumn instance using the parsed data. Note that this method does not
     * perform schema validation; it assumes the JSON is well-formed and matches the expected structure.
     *
     * @param json - A JSON string representing an enriched column object.
     * @returns An EnrichedColumn instance populated with the parsed data.
     * @throws If the JSON is malformed or cannot be parsed.
     *
     * @example
     * ```typescript
     * const json = '{"type":"string","displayName":"User Name","displayNamePlural":"User Names","order":0,"name":"user_name"}'
     * const column = EnrichedColumn.fromJSON(json)
     * ```
     */
    static fromJSON(json) {
        return new EnrichedColumn(JSON.parse(json));
    }
    /**
     * Safely creates an EnrichedColumn instance from a JSON string.
     *
     * This method parses the provided JSON string and validates its structure
     * against the enriched column schema. If the JSON is invalid or does not
     * conform to the expected schema, an error is thrown.
     *
     * @param json - A JSON string representing an enriched column object.
     * @returns An EnrichedColumn instance populated with validated data.
     * @throws If the JSON is malformed or does not match the schema.
     *
     * @example
     * ```typescript
     * const json = '{"type":"string","displayName":"User Name","displayNamePlural":"User Names","order":0,"name":"user_name"}'
     * const column = EnrichedColumn.fromJSONSafe(json)
     * ```
     */
    static fromJSONSafe(json) {
        return new EnrichedColumn(enrichedColumnSchema.parse(JSON.parse(json)));
    }
    /**
     * Converts the EnrichedColumn instance into a plain JavaScript object (POJO).
     *
     * This method transforms the EnrichedColumn class instance into a plain object
     * that contains all the column properties. This is useful for serialization,
     * transport, or storage, as it strips away class methods and returns only the data structure.
     *
     * @returns {EnrichedColumnSchema} A plain object representing the enriched column.
     *
     * @example
     * ```typescript
     * const column = new EnrichedColumn({...})
     * const pojo = column.toPOJO()
     * // Returns: { type: 'string', displayName: 'User Name', displayNamePlural: 'User Names', order: 0, name: 'user_name', ... }
     * ```
     */
    toPOJO() {
        return {
            type: this.type,
            displayName: this.displayName,
            displayNamePlural: this.displayNamePlural,
            order: this.order,
            typeConstraints: this.typeConstraints,
            guarded: this.guarded,
            hidden: this.hidden,
            cast: this.cast,
            searchable: this.searchable,
            name: this.name,
            forRelation: this.forRelation,
            fakerMethod: this.fakerMethod,
        };
    }
    /**
     * Serializes the EnrichedColumn instance to a JSON string for transport or storage.
     *
     * This method converts the EnrichedColumn instance to a plain object and then
     * stringifies it to JSON format. This is useful for sending column data over
     * networks, storing in databases, or saving to files.
     *
     * @returns {string} A JSON string representation of the EnrichedColumn.
     *
     * @example
     * ```typescript
     * const column = new EnrichedColumn({...})
     * const json = column.toJSON()
     * // Returns: '{"type":"string","displayName":"User Name","displayNamePlural":"User Names","order":0,"name":"user_name",...}'
     * ```
     */
    toJSON() {
        return JSON.stringify(this.toPOJO());
    }
    /**
     * Converts a snake_case column name into a human-readable display name.
     *
     * This static utility method transforms snake_case naming conventions
     * (e.g., "user_name") into properly formatted display names (e.g., "User Name")
     * suitable for user interfaces.
     *
     * @param name - The snake_case column name to convert.
     * @returns {string} A formatted display name suitable for UI display.
     *
     * @example
     * ```typescript
     * const displayName = EnrichedColumn.nameToDisplayName('user_email')
     * // Returns: 'User Email'
     * ```
     */
    static nameToDisplayName(name) {
        return snakeCaseToLabel(name);
    }
    /**
     * Converts a snake_case column name into a human-readable plural display name.
     *
     * This static utility method transforms snake_case naming conventions
     * (e.g., "user_name") into properly formatted plural display names (e.g., "User Names")
     * by converting to display format and appending an 's'. This is useful for UI elements
     * that need to show plural forms of column names.
     *
     * @param name - The snake_case column name to convert.
     * @returns {string} A formatted plural display name suitable for UI display.
     *
     * @example
     * ```typescript
     * const displayNamePlural = EnrichedColumn.nameToDisplayNamePlural('user_email')
     * // Returns: 'User Emails'
     * ```
     */
    static nameToDisplayNamePlural(name) {
        return snakeCaseToLabel(name) + 's';
    }
    /**
     * Sets the column name and automatically generates formatted display names.
     *
     * This method updates the column's name property and automatically generates
     * the corresponding displayName and displayNamePlural properties using the
     * static name conversion methods. This ensures consistent naming conventions
     * across the column definition.
     *
     * If the column was the model title column the model title is also updated
     * to the new name.
     *
     * @param name - The snake_case column name to set.
     *
     * @example
     * ```typescript
     * column.setNames('user_email')
     * // Results in:
     * // column.name = 'user_email'
     * // column.displayName = 'User Email'
     * // column.displayNamePlural = 'User Emails'
     * ```
     *
     * TODO: We should support name in any case style
     */
    setNames(name) {
        if (this.parentModel?.title === this.name) {
            this.parentModel.title = name;
        }
        this.name = name;
        this.displayName = EnrichedColumn.nameToDisplayName(name);
        this.displayNamePlural = EnrichedColumn.nameToDisplayNamePlural(name);
    }
    /**
     * Determines if the column must have a value.
     *
     * A column is required when it includes a `required` type constraint or
     * when it is the model's designated title column.
     *
     * @returns {boolean} True if the column is required; otherwise, false.
     *
     * @example
     * ```typescript
     * const column = new EnrichedColumn({ ... })
     * if (column.isRequired) {
     * 	// enforce presence in validators or UI
     * }
     * ```
     */
    get isRequired() {
        return (this.typeConstraints?.some((constraint) => constraint.name === 'required') || this.isTitle);
    }
    /**
     * Determines if the column may be omitted.
     *
     * This is the logical inverse of `isRequired`.
     *
     * @returns {boolean} True if the column is not required; otherwise, false.
     *
     * @example
     * ```typescript
     * const column = new EnrichedColumn({ ... })
     * if (column.isOptional) {
     * 	// allow omission; provide sensible defaults
     * }
     * ```
     */
    get isOptional() {
        return !this.isRequired;
    }
    /**
     * Indicates whether this column is the model's title column.
     *
     * Compares the parent model's `title` to this column's `name`.
     *
     * @returns {boolean} True if this column is the title column; otherwise, false.
     *
     * @example
     * ```typescript
     * const model = new EnrichedModel({ ... title: 'name' ... })
     * const column = new EnrichedColumn({ ... name: 'name' ... }, model)
     * column.isTitle // true
     * ```
     */
    get isTitle() {
        return this.parentModel?.title === this.name;
    }
}
